import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';
import { APP_REGISTRY, CATEGORIES, AppCategory } from '../../data/appRegistry';

export function CategoryAppsPage() {
  const { t } = useTranslation('apps');
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const { effectiveTheme } = useBootstrapTheme();

  const cat = CATEGORIES[category as AppCategory];
  if (!cat) return <Navigate to="/chat" replace />;

  const apps = APP_REGISTRY.filter(app => app.category === category);

  const isDark = effectiveTheme === 'dark';
  const cardBg = isDark ? '#1e293b' : '#ffffff';
  const cardBorder = isDark ? '1px solid rgba(96,165,250,0.15)' : '1px solid rgba(0,0,0,0.08)';
  const textMuted = isDark ? '#94a3b8' : '#6b7280';

  return (
    <div className="container-fluid py-4 px-4 overflow-auto" style={{ minHeight: '100%' }}>
      {/* Header */}
      <div className="mb-5">
        <div className="d-flex align-items-center gap-3 mb-2">
          <cat.icon size={40} strokeWidth={2} />
          <div>
            <h1 className="h2 mb-1 fw-bold">{t(cat.nameKey, cat.name)}</h1>
            <p className="mb-0" style={{ color: textMuted }}>{t(cat.descKey, cat.description)}</p>
          </div>
        </div>
      </div>

      {/* App Grid */}
      <div className="row g-4">
        {apps.map(app => (
          <div key={app.id} className="col-12 col-sm-6 col-lg-4 col-xl-3">
            <button
              className="w-100 text-start border-0 p-0 bg-transparent"
              onClick={() => navigate(app.route)}
              style={{ cursor: 'pointer' }}
            >
              <div
                className="h-100 rounded-3 p-4"
                style={{
                  background: cardBg,
                  border: cardBorder,
                  boxShadow: isDark
                    ? '0 2px 12px rgba(0,0,0,0.3)'
                    : '0 2px 12px rgba(0,0,0,0.06)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-3px)';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = isDark
                    ? '0 8px 24px rgba(96,165,250,0.2)'
                    : '0 8px 24px rgba(59,130,246,0.15)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLDivElement).style.transform = '';
                  (e.currentTarget as HTMLDivElement).style.boxShadow = isDark
                    ? '0 2px 12px rgba(0,0,0,0.3)'
                    : '0 2px 12px rgba(0,0,0,0.06)';
                }}
              >
                {/* App Icon */}
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 mb-3"
                  style={{
                    width: '56px',
                    height: '56px',
                    background: isDark
                      ? 'rgba(96,165,250,0.12)'
                      : 'rgba(59,130,246,0.08)',
                    color: isDark ? '#60a5fa' : '#3b82f6',
                  }}
                >
                  <app.icon size={28} strokeWidth={2} />
                </div>

                {/* App Info */}
                <div className="fw-semibold mb-1" style={{ fontSize: '1rem' }}>
                  {t(app.nameKey, app.name)}
                </div>
                <div className="small" style={{ color: textMuted, lineHeight: 1.4 }}>
                  {t(app.descKey, app.description)}
                </div>

                {/* Open button hint */}
                <div
                  className="mt-3 small fw-medium"
                  style={{ color: isDark ? '#60a5fa' : '#3b82f6' }}
                >
                  {t('registry.open')}
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
